class FloatingBuyButton {
  constructor() {
    this.floatingButton = document.getElementById('floating-buy-button');
    if (!this.floatingButton) return;

    this.mainBuyButtons = document.querySelector('.product-form__buttons');
    this.observer = null;
    this.currentSellingPlanId = null;
    this.init();
  }

  init() {
    // Set up scroll-based visibility
    this.setupObserver();

    // Listen for variant changes on main product form
    this.setupVariantSync();

    // Handle subscription toggle in floating button
    this.setupSubscriptionToggle();

    // Handle form submission
    this.setupFormSubmission();

    // Listen for subscription widget changes
    this.setupSubscriptionWidgetSync();

    // Wait for subscription widget to fully load before initializing
    this.waitForSubscriptionWidget();
  }

  waitForSubscriptionWidget() {
    let attempts = 0;
    const maxAttempts = 50; // Try for up to 10 seconds

    const checkWidget = () => {
      attempts++;
      const subscriptionWidget = document.querySelector('subscription-widget');
      const activeFrequency = document.querySelector('.subscription-widget__frequency-btn.active');
      const subscriptionPrice = document.querySelector('.subscription-widget__subscription-price');

      if (subscriptionWidget && activeFrequency && activeFrequency.dataset.planId && subscriptionPrice && subscriptionPrice.textContent) {
        // Widget is fully loaded with selling plan data and prices
        this.syncWithSubscriptionWidget();
      } else if (attempts < maxAttempts) {
        // Not loaded yet, check again
        setTimeout(checkWidget, 200);
      } else {
        console.warn('Subscription widget did not load after 10 seconds');
      }
    };

    // Start checking after a short delay
    setTimeout(checkWidget, 500);
  }

  setupObserver() {
    const scrollThreshold = 800;

    const handleScroll = () => {
      const scrollPosition = window.scrollY || window.pageYOffset;

      if (scrollPosition > scrollThreshold) {
        this.floatingButton.classList.add('visible');
      } else {
        this.floatingButton.classList.remove('visible');
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
  }

  setupVariantSync() {
    // Listen for variant changes from the main product form
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail.variant;
      if (!variant) return;

      this.updateVariant(variant);
    });
  }

  updateVariant(variant) {
    // Update hidden variant input in floating form
    const variantInput = this.floatingButton.querySelector('.floating-variant-id');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update button state
    const button = this.floatingButton.querySelector('.floating-buy-button__cta');

    if (button) {
      if (variant.available) {
        button.disabled = false;
      } else {
        button.disabled = true;
        const buttonText = button.querySelector('.floating-button-text');
        if (buttonText) {
          buttonText.textContent = 'Sold Out';
        }
      }
    }
  }

  setupSubscriptionWidgetSync() {
    // Listen for subscription widget events
    document.addEventListener('subscription:change', (event) => {
      if (event.detail) {
        const { sellingPlanId, variantId, price, type } = event.detail;

        // Update selling plan
        if (sellingPlanId !== undefined) {
          this.currentSellingPlanId = sellingPlanId;
          this.updateSellingPlan();
        }

        // Update variant if provided
        if (variantId) {
          const variantInput = this.floatingButton.querySelector('.floating-variant-id');
          if (variantInput) {
            variantInput.value = variantId;
          }
        }

        // Update price display
        if (price) {
          this.updateButtonPriceText(price);
        }

        // Sync toggle button state with main widget
        if (type) {
          this.syncToggleState(type);
        }
      }
    });

    // Watch for clicks on main subscription widget to keep floating button in sync
    this.observeSubscriptionWidget();
  }

  observeSubscriptionWidget() {
    // Watch frequency button clicks in main widget
    document.addEventListener('click', (e) => {
      const frequencyBtn = e.target.closest('.subscription-widget__frequency-btn');
      if (frequencyBtn && !frequencyBtn.closest('.floating-buy-button')) {
        // Give the widget time to update
        setTimeout(() => this.syncWithSubscriptionWidget(), 50);
      }

      // Watch purchase type radio changes
      const purchaseRadio = e.target.closest('.subscription-widget__subscription-radio, .subscription-widget__onetime-radio');
      if (purchaseRadio) {
        setTimeout(() => this.syncWithSubscriptionWidget(), 50);
      }
    });

    // Also listen for radio input changes
    document.addEventListener('change', (e) => {
      if (e.target.name === 'purchase_option') {
        setTimeout(() => this.syncWithSubscriptionWidget(), 50);
      }
    });
  }

  syncWithSubscriptionWidget() {
    const subscriptionRadio = document.querySelector('.subscription-widget__subscription-radio');
    const isSubscription = subscriptionRadio?.checked;

    // Sync toggle state
    this.syncToggleState(isSubscription ? 'subscription' : 'onetime');

    if (isSubscription) {
      // Get selling plan from active frequency button
      const activeFrequency = document.querySelector('.subscription-widget__frequency-btn.active');
      if (activeFrequency) {
        this.currentSellingPlanId = activeFrequency.dataset.planId;
        this.updateSellingPlan();
      }

      // Update price from subscription widget
      const subscriptionPrice = document.querySelector('.subscription-widget__subscription-price');
      if (subscriptionPrice?.textContent) {
        this.updateButtonPriceText(subscriptionPrice.textContent.trim());
      }
    } else {
      // One-time purchase
      this.currentSellingPlanId = null;
      this.updateSellingPlan();

      const oneTimePrice = document.querySelector('.subscription-widget__onetime-price');
      if (oneTimePrice?.textContent) {
        this.updateButtonPriceText(oneTimePrice.textContent.trim());
      }
    }

    // Sync variant (for frequency-based variants like 30/60/90/120 Servings)
    this.syncVariantWithMainForm();
  }

  syncVariantWithMainForm() {
    // Get variant ID from main product form
    const mainVariantInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]:not(.floating-variant-id)');
    const floatingVariantInput = this.floatingButton.querySelector('.floating-variant-id');

    if (mainVariantInput && floatingVariantInput) {
      floatingVariantInput.value = mainVariantInput.value;
    }
  }

  syncToggleState(type) {
    const toggleButtons = this.floatingButton.querySelectorAll('.floating-subscription-btn');

    toggleButtons.forEach(btn => {
      if (btn.dataset.type === type) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }

  formatMoney(cents) {
    const money = (cents / 100).toFixed(2);
    return `$${money}`;
  }

  setupSubscriptionToggle() {
    const toggleButtons = this.floatingButton.querySelectorAll('.floating-subscription-btn');

    toggleButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Remove active from all buttons
        toggleButtons.forEach(btn => btn.classList.remove('active'));
        // Add active to clicked button
        button.classList.add('active');

        const type = button.dataset.type;

        // Also update the main subscription widget to stay in sync
        this.updateMainSubscriptionWidget(type);

        if (type === 'subscription') {
          this.syncWithSubscriptionWidget();
        } else {
          // One-time purchase - clear selling plan
          this.currentSellingPlanId = null;
          this.updateSellingPlan();
          this.updateButtonPrice('onetime');
        }
      });
    });
  }

  updateMainSubscriptionWidget(type) {
    // Click the corresponding radio in the main widget to keep them in sync
    if (type === 'subscription') {
      const subscriptionRadio = document.querySelector('.subscription-widget__subscription-radio');
      if (subscriptionRadio && !subscriptionRadio.checked) {
        subscriptionRadio.click();
      }
    } else {
      const onetimeRadio = document.querySelector('.subscription-widget__onetime-radio');
      if (onetimeRadio && !onetimeRadio.checked) {
        onetimeRadio.click();
      }
    }
  }

  getSellingPlanFromWidget() {
    const activeFrequency = document.querySelector('.subscription-widget__frequency-btn.active');
    const sellingPlanInput = this.floatingButton.querySelector('.floating-selling-plan');

    if (!sellingPlanInput) return;

    const subscriptionRadio = document.querySelector('.subscription-widget__subscription-radio');

    if (subscriptionRadio?.checked && activeFrequency) {
      const sellingPlanId = activeFrequency.dataset.planId;
      this.currentSellingPlanId = sellingPlanId;
      sellingPlanInput.value = sellingPlanId || '';
    }
  }

  updateSellingPlan() {
    const sellingPlanInput = this.floatingButton.querySelector('.floating-selling-plan');
    const subscriptionBtn = this.floatingButton.querySelector('[data-type="subscription"]');

    if (sellingPlanInput) {
      if (subscriptionBtn?.classList.contains('active') && this.currentSellingPlanId) {
        sellingPlanInput.value = this.currentSellingPlanId;
      } else {
        sellingPlanInput.value = '';
      }
    }
  }

  updateButtonPrice(type) {
    if (type === 'subscription') {
      const subscriptionPrice = document.querySelector('.subscription-widget__subscription-price');
      if (subscriptionPrice?.textContent) {
        this.updateButtonPriceText(subscriptionPrice.textContent.trim());
      }
    } else {
      const oneTimePrice = document.querySelector('.subscription-widget__onetime-price');
      if (oneTimePrice?.textContent) {
        this.updateButtonPriceText(oneTimePrice.textContent.trim());
      }
    }
  }

  updateButtonPriceText(price) {
    const button = this.floatingButton.querySelector('.floating-buy-button__cta');
    const buttonText = button?.querySelector('.floating-button-text');

    if (buttonText && !button.disabled) {
      buttonText.textContent = `Add - ${price}`;
    }
  }

  async setupFormSubmission() {
    const form = this.floatingButton.querySelector('form');
    const button = this.floatingButton.querySelector('.floating-buy-button__cta');

    if (!form || !button) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent double submission
      if (button.disabled || button.classList.contains('loading')) {
        return;
      }

      // Sync with main form one more time before submit
      this.syncVariantWithMainForm();
      if (this.floatingButton.querySelector('[data-type="subscription"]')?.classList.contains('active')) {
        this.getSellingPlanFromWidget();
      }

      // Show loading state
      button.classList.add('loading');
      button.disabled = true;

      const formData = new FormData(form);
      const sellingPlan = formData.get('selling_plan');

      // Build item object
      const item = {
        id: parseInt(formData.get('id')),
        quantity: parseInt(formData.get('quantity'))
      };

      // Only add selling_plan if it has a value
      if (sellingPlan && sellingPlan.trim() !== '') {
        item.selling_plan = parseInt(sellingPlan);
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [item]
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        const data = await response.json();

        // Fetch updated cart
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();

        // Open cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((section) => {
            const elementToReplace = document.getElementById(section.id);
            if (elementToReplace) {
              fetch(`${window.Shopify.routes.root}?section_id=${section.section}`)
                .then(response => response.text())
                .then(responseText => {
                  const html = new DOMParser().parseFromString(responseText, 'text/html');
                  const newContent = html.getElementById(section.id);
                  if (newContent) {
                    elementToReplace.innerHTML = newContent.innerHTML;
                  }
                })
                .catch(console.error);
            }
          });

          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        } else {
          // Fallback: just reload the page
          window.location.href = window.Shopify.routes.root + 'cart';
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        // Show error state
        const span = button.querySelector('span');
        const originalText = span.textContent;
        span.textContent = 'Error - Try again';
        setTimeout(() => {
          span.textContent = originalText;
        }, 2000);
      } finally {
        // Remove loading state
        button.classList.remove('loading');
        button.disabled = false;
      }
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Singleton instance to prevent multiple initializations
let floatingButtonInstance = null;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!floatingButtonInstance) {
      floatingButtonInstance = new FloatingBuyButton();
    }
  });
} else {
  if (!floatingButtonInstance) {
    floatingButtonInstance = new FloatingBuyButton();
  }
}

// Re-initialize if Shopify theme editor reloads sections
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      if (floatingButtonInstance) {
        floatingButtonInstance.destroy();
      }
      floatingButtonInstance = new FloatingBuyButton();
    }, 100);
  });
}
